import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import {
    Phone,
    Mail,
    MapPin,
    Search,
    RefreshCw,
    CheckCircle2,
    Circle,
    Bell,
    BellOff,
    LogOut,
    Loader2,
    Trash2,
    Calendar,
    Filter,
    Package,
    User,
} from "lucide-react";
import { LOGO_URL, BUSINESS_NAME } from "@/lib/contact";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API_BASE = `${BACKEND_URL}/api`;

const TOKEN_KEY = "tps_admin_token";
const NOTIFY_PREF_KEY = "tps_admin_notify";
const SEEN_IDS_KEY = "tps_admin_seen_ids";

function getToken() {
    try {
        return localStorage.getItem(TOKEN_KEY) || "";
    } catch {
        return "";
    }
}
function setToken(t) {
    try {
        if (t) localStorage.setItem(TOKEN_KEY, t);
        else localStorage.removeItem(TOKEN_KEY);
    } catch {
        /* ignore */
    }
}

const adminApi = axios.create({
    baseURL: API_BASE,
    timeout: 20000,
    headers: { "Content-Type": "application/json" },
});

adminApi.interceptors.request.use((cfg) => {
    const t = getToken();
    if (t) cfg.headers["X-Admin-Token"] = t;
    return cfg;
});

adminApi.interceptors.response.use(
    (r) => r,
    (err) => {
        if (err?.response?.status === 401) {
            setToken("");
            if (typeof window !== "undefined") {
                window.location.href = "/admin/login";
            }
        }
        return Promise.reject(err);
    },
);

// ---------------- Login screen ----------------
function LoginScreen() {
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!password) {
            toast.error("Please enter the password.");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await adminApi.post("/admin/login", { password });
            setToken(data.token);
            window.location.href = "/admin";
        } catch (err) {
            const msg =
                err?.response?.data?.detail || "Incorrect password. Try again.";
            toast.error(typeof msg === "string" ? msg : "Login failed.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-tp-forest text-white flex items-center justify-center p-4">
            <div className="w-full max-w-sm bg-white text-tp-ink rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.35)] p-6 md:p-8">
                <div className="flex flex-col items-center gap-3">
                    <img
                        src={LOGO_URL}
                        alt={BUSINESS_NAME}
                        className="h-16 w-auto"
                    />
                    <div className="text-xs uppercase tracking-[0.18em] text-tp-forest font-semibold">
                        Admin dashboard
                    </div>
                    <h1 className="font-heading text-2xl text-tp-forest text-center leading-tight">
                        Sign in
                    </h1>
                </div>
                <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                    <div>
                        <label
                            htmlFor="password"
                            className="block text-sm font-semibold text-tp-forest mb-1"
                        >
                            Password
                        </label>
                        <input
                            id="password"
                            data-testid="admin-login-password"
                            type="password"
                            autoComplete="current-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full h-11 rounded-lg border border-tp-forest/20 bg-white px-3 text-base text-tp-ink focus:outline-none focus:ring-2 focus:ring-tp-gold focus:border-tp-gold"
                            placeholder="••••••••"
                            autoFocus
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={submitting}
                        data-testid="admin-login-submit"
                        className="tp-btn-primary w-full justify-center h-12 text-base disabled:opacity-70"
                    >
                        {submitting ? (
                            <>
                                <Loader2 size={18} className="animate-spin" />
                                Signing in…
                            </>
                        ) : (
                            "Sign in"
                        )}
                    </button>
                </form>
                <p className="mt-6 text-xs text-tp-ink/60 text-center">
                    Restricted area. Enquiries are private.
                </p>
            </div>
        </div>
    );
}

// ---------------- Utility ----------------
function relativeTime(iso) {
    if (!iso) return "";
    const now = Date.now();
    const t = Date.parse(iso);
    if (Number.isNaN(t)) return iso;
    const diff = Math.floor((now - t) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    const d = new Date(t);
    return d.toLocaleDateString("en-AU", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
}

function formatFull(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleString("en-AU", {
        weekday: "short",
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

const BIN_LABELS = {
    "2m3": "2m³",
    "3m3": "3m³",
    "4m3": "4m³",
    "6m3": "6m³",
    "8m3": "8m³",
    "Not sure": "Not sure",
};

const WASTE_LABELS = {
    "general and household": "General & household",
    "green and garden": "Green & garden",
    "mixed heavy": "Mixed heavy",
    construction: "Construction",
    commercial: "Commercial",
};

// ---------------- Dashboard ----------------
function Dashboard() {
    const [enquiries, setEnquiries] = useState([]);
    const [totalNew, setTotalNew] = useState(0);
    const [totalAll, setTotalAll] = useState(0);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [status, setStatus] = useState("all");
    const [q, setQ] = useState("");
    const [notifyEnabled, setNotifyEnabled] = useState(false);
    const seenIdsRef = useRef(new Set());
    const pollingRef = useRef(null);

    // -- Initial load and every 30s poll for new leads --
    const fetchList = async (silent = false) => {
        try {
            if (!silent) setRefreshing(true);
            const params = { limit: 500 };
            if (status !== "all") params.status = status;
            if (q.trim()) params.q = q.trim();
            const { data } = await adminApi.get("/enquiries", { params });
            const incomingIds = new Set((data.items || []).map((x) => x.id));
            // Detect NEW items (not previously seen)
            const prev = seenIdsRef.current;
            if (prev.size > 0) {
                for (const it of data.items || []) {
                    if (!prev.has(it.id) && it.contacted !== true) {
                        onNewLead(it);
                    }
                }
            }
            seenIdsRef.current = incomingIds;
            try {
                localStorage.setItem(
                    SEEN_IDS_KEY,
                    JSON.stringify([...incomingIds]),
                );
            } catch {
                /* ignore */
            }
            setEnquiries(data.items || []);
            setTotalNew(data.totalNew || 0);
            setTotalAll(data.totalAll || 0);
        } catch (err) {
            if (err?.response?.status !== 401) {
                toast.error("Couldn’t load enquiries. Retrying…");
            }
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const onNewLead = (item) => {
        toast.success(`New enquiry: ${item.name} (${item.suburb})`, {
            duration: 8000,
        });
        try {
            playChime();
        } catch {
            /* ignore */
        }
        if (
            notifyEnabled &&
            typeof Notification !== "undefined" &&
            Notification.permission === "granted"
        ) {
            try {
                const n = new Notification(
                    `New skip enquiry: ${item.name} · ${item.suburb}`,
                    {
                        body: `${BIN_LABELS[item.binSize] || item.binSize} · ${
                            WASTE_LABELS[item.wasteType] || item.wasteType
                        }\nPhone: ${item.phone}`,
                        icon: LOGO_URL,
                        tag: item.id,
                    },
                );
                n.onclick = () => {
                    window.focus();
                    n.close();
                };
            } catch {
                /* ignore */
            }
        }
    };

    // Chime via WebAudio (no external file needed)
    const playChime = () => {
        try {
            const AC = window.AudioContext || window.webkitAudioContext;
            if (!AC) return;
            const ctx = new AC();
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = "sine";
            osc.frequency.setValueAtTime(880, ctx.currentTime);
            osc.frequency.linearRampToValueAtTime(1320, ctx.currentTime + 0.15);
            gain.gain.setValueAtTime(0.0001, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.45);
            osc.connect(gain).connect(ctx.destination);
            osc.start();
            osc.stop(ctx.currentTime + 0.5);
            setTimeout(() => ctx.close(), 800);
        } catch {
            /* ignore */
        }
    };

    useEffect(() => {
        // Restore notify preference and seen IDs
        try {
            const pref = localStorage.getItem(NOTIFY_PREF_KEY) === "1";
            setNotifyEnabled(
                pref &&
                    typeof Notification !== "undefined" &&
                    Notification.permission === "granted",
            );
            const raw = localStorage.getItem(SEEN_IDS_KEY);
            if (raw) {
                const arr = JSON.parse(raw);
                if (Array.isArray(arr)) seenIdsRef.current = new Set(arr);
            }
        } catch {
            /* ignore */
        }

        fetchList();
        pollingRef.current = setInterval(() => fetchList(true), 30_000);
        return () => clearInterval(pollingRef.current);
         
    }, []);

    // Refetch when filters change
    useEffect(() => {
        fetchList();
         
    }, [status]);

    // Debounced search
    useEffect(() => {
        const t = setTimeout(() => fetchList(), 300);
        return () => clearTimeout(t);
         
    }, [q]);

    const handleNotifyToggle = async () => {
        if (typeof Notification === "undefined") {
            toast.error("Your browser doesn’t support notifications.");
            return;
        }
        if (notifyEnabled) {
            setNotifyEnabled(false);
            try {
                localStorage.setItem(NOTIFY_PREF_KEY, "0");
            } catch {
                /* ignore */
            }
            toast.info("Notifications turned off.");
            return;
        }
        try {
            const perm = await Notification.requestPermission();
            if (perm === "granted") {
                setNotifyEnabled(true);
                try {
                    localStorage.setItem(NOTIFY_PREF_KEY, "1");
                } catch {
                    /* ignore */
                }
                toast.success(
                    "Notifications on. You’ll hear a chime when new leads arrive.",
                );
            } else {
                toast.error(
                    "Notification permission blocked. Enable it in your browser settings.",
                );
            }
        } catch {
            toast.error("Couldn’t request permission.");
        }
    };

    const handleToggleContacted = async (item) => {
        try {
            const { data } = await adminApi.patch(`/enquiries/${item.id}`, {
                contacted: !item.contacted,
            });
            setEnquiries((list) =>
                list.map((x) => (x.id === item.id ? data.enquiry : x)),
            );
            setTotalNew((n) => n + (!item.contacted ? -1 : 1));
            toast.success(
                data.enquiry.contacted
                    ? "Marked as contacted."
                    : "Moved back to New.",
            );
        } catch {
            toast.error("Could not update. Try again.");
        }
    };

    const handleDelete = async (item) => {
         
        if (!window.confirm(`Delete enquiry from ${item.name}?`)) return;
        try {
            await adminApi.delete(`/enquiries/${item.id}`);
            setEnquiries((list) => list.filter((x) => x.id !== item.id));
            setTotalAll((n) => Math.max(0, n - 1));
            if (!item.contacted) setTotalNew((n) => Math.max(0, n - 1));
            toast.success("Enquiry deleted.");
        } catch {
            toast.error("Could not delete. Try again.");
        }
    };

    const handleLogout = () => {
        setToken("");
        window.location.href = "/admin/login";
    };

    const shown = useMemo(() => enquiries, [enquiries]);

    return (
        <div className="min-h-screen bg-tp-offwhite text-tp-ink">
            {/* Header */}
            <header className="sticky top-0 z-30 bg-tp-forest text-white shadow-[0_10px_24px_rgba(11,27,18,0.25)]">
                <div className="max-w-6xl mx-auto px-3 sm:px-4 py-3 flex items-center gap-3">
                    <img
                        src={LOGO_URL}
                        alt={BUSINESS_NAME}
                        className="h-10 w-auto shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.16em] text-tp-gold/90 leading-tight">
                            Admin
                        </div>
                        <div className="font-heading text-lg leading-tight truncate">
                            Enquiries
                        </div>
                    </div>
                    <button
                        onClick={handleNotifyToggle}
                        title={
                            notifyEnabled
                                ? "Notifications on — tap to turn off"
                                : "Enable browser notifications"
                        }
                        data-testid="admin-notify-toggle"
                        className="p-2 rounded-md hover:bg-white/10 tp-focus"
                    >
                        {notifyEnabled ? (
                            <Bell size={18} className="text-tp-gold" />
                        ) : (
                            <BellOff size={18} className="text-white/70" />
                        )}
                    </button>
                    <button
                        onClick={() => fetchList()}
                        title="Refresh"
                        data-testid="admin-refresh"
                        className="p-2 rounded-md hover:bg-white/10 tp-focus"
                    >
                        <RefreshCw
                            size={18}
                            className={refreshing ? "animate-spin" : ""}
                        />
                    </button>
                    <button
                        onClick={handleLogout}
                        title="Sign out"
                        data-testid="admin-logout"
                        className="p-2 rounded-md hover:bg-white/10 tp-focus"
                    >
                        <LogOut size={18} />
                    </button>
                </div>

                {/* Filters + search */}
                <div className="max-w-6xl mx-auto px-3 sm:px-4 pb-3 flex flex-col sm:flex-row gap-2">
                    <div className="relative flex-1">
                        <Search
                            size={16}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50"
                        />
                        <input
                            data-testid="admin-search"
                            type="search"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search name, phone, suburb, email…"
                            className="w-full h-10 pl-9 pr-3 rounded-md bg-white/10 border border-white/15 placeholder:text-white/50 text-sm text-white focus:outline-none focus:ring-2 focus:ring-tp-gold"
                        />
                    </div>
                    <div
                        role="tablist"
                        className="inline-flex bg-white/10 border border-white/15 rounded-md p-0.5 self-start"
                    >
                        {[
                            {
                                key: "all",
                                label: `All (${totalAll})`,
                                testid: "admin-filter-all",
                            },
                            {
                                key: "new",
                                label: `New (${totalNew})`,
                                testid: "admin-filter-new",
                            },
                            {
                                key: "contacted",
                                label: "Contacted",
                                testid: "admin-filter-contacted",
                            },
                        ].map((tab) => (
                            <button
                                key={tab.key}
                                onClick={() => setStatus(tab.key)}
                                data-testid={tab.testid}
                                aria-selected={status === tab.key}
                                className={`px-3 py-1.5 text-xs font-semibold rounded transition-colors ${
                                    status === tab.key
                                        ? "bg-tp-gold text-tp-forest"
                                        : "text-white/85 hover:bg-white/10"
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>
            </header>

            {/* List */}
            <main className="max-w-6xl mx-auto px-3 sm:px-4 py-4">
                {loading ? (
                    <div className="py-16 flex items-center justify-center gap-2 text-tp-ink/60">
                        <Loader2
                            size={18}
                            className="animate-spin text-tp-forest"
                        />
                        Loading enquiries…
                    </div>
                ) : shown.length === 0 ? (
                    <div
                        data-testid="admin-empty"
                        className="py-16 text-center rounded-2xl bg-white border border-tp-forest/10"
                    >
                        <div className="mx-auto inline-flex items-center justify-center h-12 w-12 rounded-full bg-tp-forest/5 ring-1 ring-tp-gold/40">
                            <Filter
                                size={22}
                                strokeWidth={2.2}
                                className="text-tp-forest"
                            />
                        </div>
                        <h3 className="mt-4 font-heading text-xl text-tp-forest">
                            No enquiries match
                        </h3>
                        <p className="mt-1 text-sm text-tp-ink/70">
                            Try clearing your search or changing the filter.
                        </p>
                    </div>
                ) : (
                    <ul
                        data-testid="admin-enquiries-list"
                        className="grid gap-3"
                    >
                        {shown.map((item) => (
                            <li
                                key={item.id}
                                data-testid={`enquiry-${item.id}`}
                                className={`bg-white rounded-2xl border ${
                                    item.contacted
                                        ? "border-tp-forest/10"
                                        : "border-l-4 border-l-tp-gold border-tp-forest/10"
                                } shadow-[0_10px_30px_rgba(20,83,45,0.06)]`}
                            >
                                <div className="p-4 sm:p-5">
                                    <div className="flex items-start gap-3">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <User
                                                    size={16}
                                                    className="text-tp-forest shrink-0"
                                                />
                                                <h3 className="font-heading text-lg text-tp-forest leading-tight break-words">
                                                    {item.name}
                                                </h3>
                                                {!item.contacted && (
                                                    <span className="text-[10px] uppercase tracking-[0.14em] rounded-full bg-tp-gold text-tp-forest px-2 py-0.5 font-bold">
                                                        New
                                                    </span>
                                                )}
                                            </div>
                                            <div
                                                className="mt-1 text-xs text-tp-ink/60"
                                                title={formatFull(item.createdAt)}
                                            >
                                                {relativeTime(item.createdAt)}
                                                <span className="mx-2">·</span>
                                                {formatFull(item.createdAt)}
                                            </div>
                                        </div>
                                        <button
                                            onClick={() =>
                                                handleToggleContacted(item)
                                            }
                                            data-testid={`toggle-contacted-${item.id}`}
                                            className={`shrink-0 inline-flex items-center gap-1.5 rounded-lg px-3 h-9 text-xs font-bold border tp-focus transition-colors ${
                                                item.contacted
                                                    ? "bg-tp-recycle/15 border-tp-recycle/40 text-tp-forest"
                                                    : "bg-white border-tp-forest/20 text-tp-forest hover:bg-tp-forest/5"
                                            }`}
                                        >
                                            {item.contacted ? (
                                                <>
                                                    <CheckCircle2
                                                        size={14}
                                                        className="text-tp-recycle"
                                                    />
                                                    Contacted
                                                </>
                                            ) : (
                                                <>
                                                    <Circle size={14} />
                                                    Mark done
                                                </>
                                            )}
                                        </button>
                                    </div>

                                    <div className="mt-3 grid gap-2 sm:grid-cols-2">
                                        <a
                                            href={`tel:${item.phone.replace(/\s+/g, "")}`}
                                            data-testid={`enquiry-call-${item.id}`}
                                            className="tp-btn-primary text-sm h-11 justify-center tp-focus"
                                        >
                                            <Phone size={15} strokeWidth={2.6} />
                                            Call {item.phone}
                                        </a>
                                        <a
                                            href={`mailto:${item.email}?subject=${encodeURIComponent(
                                                "Your skip bin enquiry — Tipping Point Skips",
                                            )}`}
                                            data-testid={`enquiry-email-${item.id}`}
                                            className="inline-flex items-center gap-1.5 justify-center h-11 rounded-lg border border-tp-forest/20 text-sm text-tp-forest font-semibold hover:bg-tp-forest/5 tp-focus"
                                        >
                                            <Mail size={15} strokeWidth={2.4} />
                                            Email {item.email}
                                        </a>
                                    </div>

                                    <dl className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                                        <div>
                                            <dt className="text-tp-ink/50 uppercase tracking-wider text-[10px]">
                                                <MapPin
                                                    size={11}
                                                    className="inline mr-1"
                                                />
                                                Suburb
                                            </dt>
                                            <dd className="mt-0.5 font-semibold text-tp-ink">
                                                {item.suburb}
                                            </dd>
                                        </div>
                                        <div>
                                            <dt className="text-tp-ink/50 uppercase tracking-wider text-[10px]">
                                                <Package
                                                    size={11}
                                                    className="inline mr-1"
                                                />
                                                Bin size
                                            </dt>
                                            <dd className="mt-0.5 font-semibold text-tp-ink">
                                                {BIN_LABELS[item.binSize] ||
                                                    item.binSize}
                                            </dd>
                                        </div>
                                        <div>
                                            <dt className="text-tp-ink/50 uppercase tracking-wider text-[10px]">
                                                Waste
                                            </dt>
                                            <dd className="mt-0.5 font-semibold text-tp-ink">
                                                {WASTE_LABELS[item.wasteType] ||
                                                    item.wasteType}
                                            </dd>
                                        </div>
                                        <div>
                                            <dt className="text-tp-ink/50 uppercase tracking-wider text-[10px]">
                                                <Calendar
                                                    size={11}
                                                    className="inline mr-1"
                                                />
                                                Preferred
                                            </dt>
                                            <dd className="mt-0.5 font-semibold text-tp-ink">
                                                {item.preferredDate || "—"}
                                            </dd>
                                        </div>
                                    </dl>

                                    {item.message && (
                                        <div className="mt-3 rounded-lg bg-tp-offwhite border-l-4 border-tp-gold px-3 py-2 text-sm text-tp-ink">
                                            {item.message}
                                        </div>
                                    )}

                                    <div className="mt-3 flex items-center justify-between gap-2">
                                        <div className="text-[11px] text-tp-ink/40">
                                            ID {item.id.slice(0, 8)}
                                        </div>
                                        <button
                                            onClick={() => handleDelete(item)}
                                            data-testid={`enquiry-delete-${item.id}`}
                                            className="inline-flex items-center gap-1 text-xs text-tp-ink/50 hover:text-red-600 transition-colors"
                                            title="Delete enquiry"
                                        >
                                            <Trash2 size={13} />
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}

                <p className="mt-8 text-center text-xs text-tp-ink/40">
                    Auto-refreshes every 30 seconds · Tipping Point Skips admin
                </p>
            </main>
        </div>
    );
}

// ---------------- Top-level export ----------------
export const AdminPage = ({ page }) => {
    const [ready, setReady] = useState(false);
    const [hasSession, setHasSession] = useState(false);

    useEffect(() => {
        const t = getToken();
        if (!t) {
            setHasSession(false);
            setReady(true);
            return;
        }
        // Verify token with server
        adminApi
            .get("/admin/session")
            .then(() => setHasSession(true))
            .catch(() => setHasSession(false))
            .finally(() => setReady(true));
    }, []);

    if (!ready) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-tp-offwhite">
                <Loader2
                    size={22}
                    className="animate-spin text-tp-forest"
                />
            </div>
        );
    }

    if (page === "login" || !hasSession) {
        return <LoginScreen />;
    }
    return <Dashboard />;
};

export default AdminPage;
